import React, { memo } from 'react';

interface MetricCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  accentColor: 'solar' | 'load' | 'export' | 'import';
  subtitle?: string;
  onClick?: () => void;
}

const colorClasses = {
  solar: {
    bg: 'from-amber-500/20 to-orange-500/10',
    border: 'border-amber-500/30',
    icon: 'text-amber-400',
    value: 'text-amber-400',
  },
  load: {
    bg: 'from-orange-500/20 to-red-500/10',
    border: 'border-orange-500/30',
    icon: 'text-orange-400',
    value: 'text-orange-400',
  },
  export: {
    bg: 'from-green-500/20 to-emerald-500/10',
    border: 'border-green-500/30',
    icon: 'text-green-400',
    value: 'text-green-400',
  },
  import: {
    bg: 'from-red-500/20 to-rose-500/10',
    border: 'border-red-500/30',
    icon: 'text-red-400',
    value: 'text-red-400',
  },
};

export const MetricCard = memo(function MetricCard({ title, value, icon, accentColor, subtitle, onClick }: MetricCardProps) {
  const colors = colorClasses[accentColor];

  return (
    <div
      onClick={onClick}
      className={`
        relative overflow-hidden rounded-xl border ${colors.border}
        bg-gradient-to-br ${colors.bg} backdrop-blur-sm
        p-4 sm:p-6 transition-all duration-200
        ${onClick ? 'cursor-pointer hover:scale-[1.02] hover:shadow-lg' : ''}
      `}
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 opacity-10">
        <div className={`w-full h-full ${colors.icon}`}>{icon}</div>
      </div>

      <div className="relative">
        {/* Icon */}
        <div className={`${colors.icon} mb-2 sm:mb-3 [&>svg]:w-6 [&>svg]:h-6 sm:[&>svg]:w-8 sm:[&>svg]:h-8`}>{icon}</div>

        {/* Title */}
        <p className="text-xs sm:text-sm font-medium text-slate-400 uppercase tracking-wide mb-1">
          {title}
        </p>

        {/* Value */}
        <p className={`text-2xl sm:text-4xl font-bold tracking-tight ${colors.value}`}>
          {value}
        </p>

        {/* Subtitle */}
        {subtitle && (
          <p className="text-xs sm:text-sm text-slate-500 mt-1">{subtitle}</p>
        )}
      </div>
    </div>
  );
});

export default MetricCard;
